import { useState } from "react";
import { motion } from "framer-motion";
import { 
  Users, 
  Bell, 
  Settings, 
  Plus,
  Search,
  Filter,
  Download,
  Calendar,
  DollarSign,
  Activity,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  AlertTriangle, 
  Layers, 
  Eye, 
  MousePointer,
  X,
  ChevronDown
} from "lucide-react";

// Animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.08 } }
};

// Desktop Annotation
const Annotation = ({ children, className = "" }) => (
  <span className={`annotation whitespace-nowrap ${className}`}>
    {children}
  </span>
);

// Mobile: Cropped vignette - TOP-LEFT QUADRANT ONLY
// Shows the chaos clearly with readable text (14-16px equivalent)
const DashboardVignetteMobile = () => {
  const [showIssues, setShowIssues] = useState(false);
  
  return (
    <div className="md:hidden">
      {/* Cropped vignette - only top-left chaos area */}
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        {/* Header row - cramped with too many actions */}
        <div className="bg-secondary/50 px-3 py-2.5 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-heading font-semibold text-foreground text-sm">Dashboard</span>
              <span className="text-[11px] bg-problem-light text-problem-foreground px-1.5 py-0.5 rounded-full">Pro</span>
              <span className="text-[11px] bg-muted px-1.5 py-0.5 rounded-full">Beta</span>
            </div>
            <div className="flex items-center gap-1">
              <button className="p-1.5 text-muted-foreground"><Search size={14} /></button>
              <button className="p-1.5 text-muted-foreground"><Filter size={14} /></button>
              <button className="p-1.5 text-muted-foreground"><Bell size={14} /></button>
            </div>
          </div>
        </div>
        
        {/* Metric cards - too many competing */}
        <div className="p-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-secondary rounded-md p-2.5 border border-border">
              <div className="flex items-center justify-between mb-1">
                <DollarSign size={14} className="text-muted-foreground" />
                <span className="text-[11px] text-improvement flex items-center">
                  <ArrowUpRight size={11} />+12%
                </span>
              </div>
              <p className="font-heading font-bold text-foreground text-base">$24,521</p>
              <p className="text-xs text-muted-foreground">Revenue</p>
            </div>
            <div className="bg-secondary rounded-md p-2.5 border border-border">
              <div className="flex items-center justify-between mb-1">
                <Users size={14} className="text-muted-foreground" />
                <span className="text-[11px] text-improvement flex items-center">
                  <ArrowUpRight size={11} />+8%
                </span>
              </div>
              <p className="font-heading font-bold text-foreground text-base">1,429</p>
              <p className="text-xs text-muted-foreground">Users</p>
            </div>
            <div className="bg-secondary rounded-md p-2.5 border border-border">
              <div className="flex items-center justify-between mb-1">
                <Activity size={14} className="text-muted-foreground" />
                <span className="text-[11px] text-problem flex items-center">
                  <ArrowDownRight size={11} />-3%
                </span>
              </div>
              <p className="font-heading font-bold text-foreground text-base">8,942</p>
              <p className="text-xs text-muted-foreground">Sessions</p>
            </div>
            <div className="bg-secondary rounded-md p-2.5 border border-border">
              <div className="flex items-center justify-between mb-1">
                <PieChart size={14} className="text-muted-foreground" />
                <span className="text-[11px] text-problem flex items-center">
                  <ArrowUpRight size={11} />+2%
                </span>
              </div>
              <p className="font-heading font-bold text-foreground text-base">42.1%</p>
              <p className="text-xs text-muted-foreground">Bounce</p>
            </div>
          </div>
          
          {/* Urgent indicators - everything looks urgent */}
          <div className="flex items-center gap-3 mt-3 pt-2.5 border-t border-border">
            <span className="text-xs text-problem flex items-center gap-1">
              <span className="w-2 h-2 bg-problem rounded-full animate-pulse" />
              3 urgent
            </span>
            <span className="text-xs text-problem flex items-center gap-1">
              <span className="w-2 h-2 bg-problem rounded-full" />
              5 warnings
            </span>
            <span className="text-xs text-muted-foreground">12 notifs</span>
          </div>
        </div>
      </div>
      
      {/* Issues toggle - collapsible */}
      <button 
        onClick={() => setShowIssues(!showIssues)}
        className="flex items-center gap-1.5 mt-3 text-xs text-muted-foreground"
      >
        <ChevronDown size={14} className={`transition-transform ${showIssues ? 'rotate-180' : ''}`} />
        {showIssues ? 'Hide issues' : 'View identified issues'}
      </button>
      {showIssues && (
        <div className="mt-2 flex flex-wrap gap-2">
          <Annotation>Too many actions</Annotation>
          <Annotation>No clear priority</Annotation>
          <Annotation>Everything urgent</Annotation>
        </div>
      )}
    </div>
  );
};

// Desktop: Full dashboard with inline annotations
const DashboardDesktop = () => (
  <div className="hidden md:block bg-card rounded-md p-4 border border-border relative">
    {/* Header */}
    <div className="flex items-center justify-between mb-3 relative">
      <div className="flex items-center gap-2">
        <span className="font-heading font-semibold text-foreground text-sm">Dashboard</span>
        <span className="text-[10px] bg-problem-light text-problem-foreground px-1.5 py-0.5 rounded-full">Pro</span>
        <span className="text-[10px] bg-secondary px-1.5 py-0.5 rounded-full">Beta</span>
      </div>
      <div className="flex items-center gap-1 relative">
        {[Search, Filter, Download, Calendar, Bell, Settings].map((Icon, i) => (
          <button key={i} className="p-1.5 text-muted-foreground hover:bg-secondary rounded"><Icon size={13} /></button>
        ))}
        <button className="px-2 py-1 bg-primary text-primary-foreground text-[10px] rounded flex items-center gap-0.5">
          <Plus size={10} />New
        </button>
        <button className="px-2 py-1 bg-secondary text-[10px] rounded">Export</button>
        <button className="px-2 py-1 bg-secondary text-[10px] rounded">Share</button>
        <div className="absolute -top-1 -right-2 translate-x-full">
          <Annotation>Too many primary actions</Annotation>
        </div>
      </div>
    </div>

    {/* Metrics */}
    <div className="grid grid-cols-6 gap-2 mb-3 relative">
      {[
        { label: "Revenue", value: "$24,521", icon: DollarSign, change: "+12%", up: true },
        { label: "Users", value: "1,429", icon: Users, change: "+8%", up: true },
        { label: "Sessions", value: "8,942", icon: Activity, change: "-3%", up: false },
        { label: "Bounce Rate", value: "42.1%", icon: PieChart, change: "+2%", up: false },
        { label: "Avg. Duration", value: "4m 23s", icon: Calendar, change: "+15%", up: true },
        { label: "Conversions", value: "342", icon: PieChart, change: "+5%", up: true },
      ].map((stat, i) => (
        <div key={i} className="dashboard-card p-2">
          <div className="flex items-center justify-between mb-0.5">
            <stat.icon size={11} className="text-muted-foreground" />
            <span className={`text-[9px] flex items-center ${stat.up ? 'text-improvement' : 'text-problem'}`}>
              {stat.up ? <ArrowUpRight size={9} /> : <ArrowDownRight size={9} />}
              {stat.change}
            </span>
          </div>
          <p className="font-heading font-semibold text-foreground text-sm">{stat.value}</p>
          <p className="text-[9px] text-muted-foreground">{stat.label}</p>
        </div>
      ))}
      <div className="absolute -bottom-1 left-4 translate-y-full">
        <Annotation>No clear priority</Annotation>
      </div>
    </div>

    {/* Content grid */}
    <div className="grid grid-cols-3 gap-3 relative">
      <div className="col-span-2 dashboard-card p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-foreground">Analytics Overview</span>
          <div className="flex gap-0.5">
            {["Day", "Week", "Month", "Year"].map((t, i) => (
              <button key={i} className={`px-1.5 py-0.5 text-[9px] rounded ${i === 1 ? 'bg-primary text-primary-foreground' : 'bg-secondary'}`}>{t}</button>
            ))}
          </div>
        </div>
        <div className="flex items-end justify-between h-20 gap-1">
          {[65, 45, 78, 52, 88, 42, 95, 67, 73, 58, 82, 48].map((h, i) => (
            <div key={i} className="flex-1 bg-primary/20 rounded-t-sm" style={{ height: `${h}%` }} />
          ))}
        </div>
      </div>
      
      <div className="space-y-2 relative">
        <div className="dashboard-card p-2">
          <span className="text-[9px] font-medium text-muted-foreground">Quick Actions</span>
          <div className="grid grid-cols-2 gap-1 mt-1">
            {["Add User", "Report", "Export", "Settings", "Invite", "Archive"].map((a, i) => (
              <button key={i} className="text-[9px] p-1 bg-secondary rounded">{a}</button>
            ))}
          </div>
        </div>
        <div className="dashboard-card p-2">
          <span className="text-[9px] font-medium text-muted-foreground">Activity</span>
          <div className="mt-1 space-y-0.5">
            {["User signed up", "Report generated", "Payment received"].map((a, i) => (
              <div key={i} className="flex items-center gap-1 text-[9px] text-muted-foreground">
                <span className="w-1 h-1 rounded-full bg-muted-foreground/50" />{a}
              </div>
            ))}
          </div>
        </div>
        <div className="absolute top-1/2 -right-2 translate-x-full -translate-y-1/2">
          <Annotation>Important info buried</Annotation>
        </div>
      </div>
    </div>

    {/* Bottom */}
    <div className="mt-3 pt-2 border-t border-border flex items-center justify-between relative">
      <div className="flex items-center gap-3">
        <span className="text-[10px] text-problem flex items-center gap-1">
          <span className="w-1.5 h-1.5 bg-problem rounded-full animate-pulse" />3 urgent
        </span>
        <span className="text-[10px] text-problem flex items-center gap-1">
          <span className="w-1.5 h-1.5 bg-problem rounded-full" />5 warnings
        </span>
        <span className="text-[10px] text-muted-foreground">12 notifications</span>
      </div>
      <button className="text-[10px] text-primary">View all</button>
      <div className="absolute -bottom-1 right-0 translate-y-full">
        <Annotation>Everything looks urgent</Annotation>
      </div>
    </div>
  </div>
);

// Friction card component
const FrictionCard = ({ icon: Icon, title, description, type }) => {
  const colors = {
    overload: { bg: 'bg-problem-light', icon: 'text-problem' },
    redundant: { bg: 'bg-secondary', icon: 'text-secondary-foreground' },
    unclear: { bg: 'bg-warning/10', icon: 'text-warning' },
    hidden: { bg: 'bg-muted', icon: 'text-muted-foreground' }
  };
  const c = colors[type];
  
  return (
    <div className="friction-card flex items-start gap-3">
      <div className={`p-2 rounded ${c.bg}`}>
        <Icon size={16} className={c.icon} />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-heading font-semibold text-foreground text-sm">{title}</h4>
        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{description}</p>
      </div>
    </div>
  );
};

// Scorecard item - icon aligned to baseline
const ScorecardItem = ({ children }) => (
  <div className="flex items-baseline gap-2 py-1">
    <div className="w-4 h-4 rounded-full bg-problem-light flex items-center justify-center flex-shrink-0 relative top-[2px]">
      <X size={10} className="text-problem" />
    </div>
    <span className="text-sm text-muted-foreground leading-snug">{children}</span>
  </div>
);

// Main component
export const DiagnosisZone = () => {
  const frictionItems = [
    { type: "overload", icon: Layers, title: "Decision Overload", description: "Too many choices presented simultaneously." },
    { type: "redundant", icon: Eye, title: "Redundant Information", description: "Same data in multiple places with different labels." },
    { type: "unclear", icon: MousePointer, title: "Unclear Next Step", description: "No obvious primary action for users." },
    { type: "hidden", icon: AlertTriangle, title: "Hidden Critical Actions", description: "Important functions buried under menus." }
  ];

  return (
    <section id="problem" className="zone-diagnosis relative">
      {/* Narrative connector line - desktop only */}
      <div className="narrative-line" style={{ top: '2rem', bottom: '2rem' }} />
      
      <div className="container-wide py-6 md:py-10 lg:pl-12">
        {/* Section header - tighter mobile spacing */}
        <motion.div 
          initial="hidden" 
          whileInView="visible" 
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
          className="mb-4 md:mb-6"
        >
          <p className="text-[11px] font-medium text-problem tracking-wide uppercase mb-1">The Problem</p>
          <h2 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-foreground mb-1.5 md:mb-2">
            Where Products Go Wrong
          </h2>
          <p className="text-sm text-muted-foreground max-w-xl leading-relaxed">
            A typical dashboard that tries to show everything at once. 
            Nothing is broken — but everything competes for attention.
          </p>
        </motion.div>

        {/* Dashboard mockup */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeUp}
        >
          <DashboardVignetteMobile />
          <DashboardDesktop />
        </motion.div>

        {/* Friction Indicators - overlapping the dashboard on desktop */}
        <div id="friction" className="mt-5 md:overlap-up relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeUp}
            className="mb-3 md:mb-4"
          >
            <p className="text-[11px] font-medium text-muted-foreground tracking-wide uppercase mb-1">Analysis</p>
            <h3 className="font-heading text-lg md:text-xl lg:text-2xl font-bold text-foreground">
              Friction Indicators
            </h3>
          </motion.div>

          {/* 2x2 grid + scorecard layout */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={stagger}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
          >
            {/* Friction cards */}
            {frictionItems.map((item, i) => (
              <motion.div key={i} variants={fadeUp}>
                <FrictionCard {...item} />
              </motion.div>
            ))}
            
            {/* Scorecard - spans 2 columns on large screens */}
            <motion.div variants={fadeUp} className="md:col-span-2 lg:col-span-2 lg:row-span-2 lg:col-start-2 lg:row-start-1">
              <div className="scorecard h-full flex flex-col">
                <div className="scorecard-header">
                  <span className="text-[10px] font-medium text-problem uppercase tracking-wider">Review Required</span>
                  <h4 className="font-heading font-semibold text-foreground text-sm mt-0.5">
                    Clarity Check — Action Required
                  </h4>
                </div>
                <div className="p-4 flex-1">
                  <div className="space-y-0">
                    <ScorecardItem>Can a user tell what to do in 5 seconds?</ScorecardItem>
                    <ScorecardItem>Is there one clear primary action?</ScorecardItem>
                    <ScorecardItem>Are numbers explained or just displayed?</ScorecardItem>
                    <ScorecardItem>Is navigation predictable and minimal?</ScorecardItem>
                    <ScorecardItem>Does every element earn its place?</ScorecardItem>
                  </div>
                  <div className="mt-4 pt-3 border-t border-border">
                    <p className="text-xs text-muted-foreground italic leading-relaxed">
                      This step requires judgment. Not everything that looks unnecessary actually is.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default DiagnosisZone;
